"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import {
  type ChatMessage,
  WELCOME_MESSAGE,
} from "./support-chat-utils";

export function useSupportChat(autoInit = false) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const messagesRef = useRef<ChatMessage[]>(messages);

  messagesRef.current = messages;

  const initChat = useCallback(() => {
    if (initialized) return;
    setMessages([{ id: "welcome", role: "assistant", content: WELCOME_MESSAGE }]);
    setInitialized(true);
  }, [initialized]);

  useEffect(() => {
    if (autoInit) initChat();
  }, [autoInit, initChat]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      if (!initialized) initChat();

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
      };

      setMessages(prev => [...prev, userMsg]);
      setInput("");
      setLoading(true);

      const history = [...messagesRef.current, userMsg]
        .filter(m => m.id !== "welcome")
        .map(m => ({ role: m.role, content: m.content }));

      try {
        const res = await fetch("/api/ai/support-chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: trimmed, history }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "답변을 받지 못했습니다.");
        }

        setMessages(prev => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content: data.message,
          },
        ]);
      } catch (err) {
        setMessages(prev => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: "assistant",
            content:
              err instanceof Error
                ? `${err.message}\n\n직접 문의가 필요하면 아래 '1:1 문의'를 이용해주세요.`
                : "오류가 발생했어요. 잠시 후 다시 시도하거나 1:1 문의를 이용해주세요.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, initialized, initChat]
  );

  return {
    messages,
    input,
    setInput,
    loading,
    initialized,
    initChat,
    sendMessage,
  };
}
